import { notificationsLinkMap } from "@clovis/server/src/app/domain/notification/notification-map/user-notifications/links";
import type { OrgRoleId } from "@clovis/server/src/app/domain/org/role";
import type { ProjectRoleId } from "@clovis/server/src/app/domain/project/role";
import * as React from "react";
import { Trans } from "react-i18next";
import type { UserNotification } from "~/config/user/user-notifications/utils/types";
import type { UserContextType } from "~/config/user/UserProvider";
import { Text } from "~/design-system/Text";
import { RoleTranslation } from "./components/RoleTranslation";

/* IMPORTANT: This file is both used in the FRONTEND to display a Notification object 
   but also in the BACKEND to generate the string for push Notfications */

type NotificationMap<T extends UserNotification["type"]> = {
  [K in T]: (
    ctx: { user: UserContextType["user"] },
    notification: Extract<UserNotification, { type: K }>
  ) => { content: React.ReactNode; link: string }; // Extract the union member that has { type: K }
};

function createNotificationsMap<T extends UserNotification["type"]>(
  map: NotificationMap<T>
) {
  return map;
}

// TODO: find a way to be exhaustive on the keys with typescript (if we add a type in UserNotification, notificationMap shouldn't type-check)
const notificationsMap = createNotificationsMap({
  "ORG/INFO/ORG_PROJECT_ARCHIVE_UPDATED": (_ctx, notification) => {
    return {
      content: notification.payload.project.isArchived ? (
        <Trans
          i18nKey="notifications.ORG/INFO/ORG_PROJECT_ARCHIVE_UPDATED.archived"
          defaults="{{creator.firstName}} archived <Highlight>{{project.name}}</Highlight> project"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ) : (
        <Trans
          i18nKey="notifications.ORG/INFO/ORG_PROJECT_ARCHIVE_UPDATED.unarchived"
          defaults="{{creator.firstName}} unarchived <Highlight>{{project.name}}</Highlight> project"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "ORG/PROJECT_CREATED_FROM_SPREADSHEET": (_ctx, notification) => {
    const skippedExistingProjectNames =
      notification.payload.skippedExistingProjectNames ?? [];
    const skippedExistingProjectsCount = skippedExistingProjectNames.length;
    const projectsCreatedLabel =
      notification.payload.projectsCreated === 1 ? "project" : "projects";
    const skippedExistingProjectsLabel =
      skippedExistingProjectsCount === 1 ? "project" : "projects";
    const skippedExistingProjectsVerb =
      skippedExistingProjectsCount === 1 ? "was" : "were";

    return {
      content:
        skippedExistingProjectsCount > 0 ? (
          <Trans
            i18nKey={
              notification.payload.projectsCreated > 0
                ? "notifications.ORG/PROJECT_CREATED_FROM_SPREADSHEET.anotherWithSkipped"
                : "notifications.ORG/PROJECT_CREATED_FROM_SPREADSHEET.onlySkipped"
            }
            defaults={
              notification.payload.projectsCreated > 0
                ? "{{creator.firstName}} has created <Highlight>{{projectsCreated}}</Highlight> {{projectsCreatedLabel}}. <Warning>{{skippedExistingProjectsCount}}</Warning> {{skippedExistingProjectsLabel}} already existed and {{skippedExistingProjectsVerb}} skipped."
                : "No projects were created. <Warning>{{skippedExistingProjectsCount}}</Warning> {{skippedExistingProjectsLabel}} already existed and {{skippedExistingProjectsVerb}} skipped."
            }
            values={{
              ...notification.payload,
              projectsCreatedLabel,
              skippedExistingProjectsCount,
              skippedExistingProjectsLabel,
              skippedExistingProjectsVerb,
            }}
            components={{
              Highlight: <Text size="inherit" weight="medium" tone="info" />,
              Warning: <Text size="inherit" weight="medium" tone="caution" />,
            }}
          />
        ) : (
          <Trans
            i18nKey="notifications.ORG/PROJECT_CREATED_FROM_SPREADSHEET.another"
            defaults="{{creator.firstName}} has created <Highlight>{{projectsCreated}}</Highlight>"
            values={notification.payload}
            components={{
              Highlight: <Text size="inherit" weight="medium" tone="info" />,
            }}
          />
        ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "ORG/PROJECT_SPREADSHEET_IMPORT_REJECTED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.ORG/PROJECT_SPREADSHEET_IMPORT_REJECTED.limitExceeded"
          defaults="Project import was not processed. The spreadsheet contains <Warning>{{projectsInSpreadsheet}}</Warning> projects, but the maximum is <Warning>{{maxProjects}}</Warning>. Please split the file and try again."
          values={notification.payload}
          components={{
            Warning: <Text size="inherit" weight="medium" tone="caution" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "ORG/ROLE_UPDATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.ORG/ROLE_UPDATED.me"
          defaults="{{creator.firstName}} updated your role from <Highlight><OldRole /></Highlight> to <Highlight><NewRole /></Highlight> ({{org.name}} org)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
            NewRole: (
              <RoleTranslation
                type="ORG"
                role={notification.payload.newRole as OrgRoleId}
              />
            ),
            OldRole: (
              <RoleTranslation
                type="ORG"
                role={notification.payload.oldRole as OrgRoleId}
              />
            ),
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "ORG/USER_INVITED_TO_ORG": (ctx, notification) => {
    return {
      content:
        ctx.user.id === notification.payload.invited.id ? (
          <Trans
            i18nKey="notifications.ORG/USER_INVITED_TO_ORG.me"
            defaults="{{creator.firstName}} invited you to <Highlight>{{org.name}}</Highlight>"
            values={notification.payload}
            components={{
              Highlight: <Text size="inherit" weight="medium" tone="info" />,
            }}
          />
        ) : (
          <Trans
            i18nKey="notifications.ORG/USER_INVITED_TO_ORG.another"
            defaults="{{creator.firstName}} invited {{invited.firstName}} to <Highlight>{{org.name}}</Highlight>"
            values={notification.payload}
            components={{
              Highlight: <Text size="inherit" weight="medium" tone="info" />,
            }}
          />
        ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/CHAT/CHANNEL_CREATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.CHAT/PROJECT_CHANNEL_CREATED"
          defaults="{{creator.firstName}} created chat channel {{channel.name}}"
          values={notification.payload}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/CHAT/CHANNEL_MERMBERS_ADDED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/CHAT/CHANNEL_MERMBERS_ADDED"
          defaults="invited you to chat channel {{channel.name}}"
          values={notification.payload}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/CHAT/CHANNEL_RENAMED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/CHAT/CHANNEL_RENAMED"
          defaults="{{creator.firstName}} renamed chat channel on {{project.name}}"
          values={notification.payload}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/CHAT/FILE_APPROVALS/MESSAGE_RECEIVED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="PROJECT/CHAT/FILE_APPROVALS/MESSAGE_RECEIVED"
          defaults="{{creator.firstName}} left a message on a file approvals"
          values={notification.payload}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/CHAT/MESSAGE_RECEIVED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/CHAT/MESSAGE_RECEIVED"
          defaults="{{creator.firstName}} left a message on chat"
          values={notification.payload}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/CHAT/PROJECT_DIRECT_MESSAGE_RECEIVED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/CHAT/PROJECT_DIRECT_MESSAGE_RECEIVED"
          defaults="{{creator.firstName}} left you a direct message"
          values={notification.payload}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/CHAT/TASK/MESSAGE_RECEIVED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="PROJECT/CHAT/TASK/MESSAGE_RECEIVED"
          defaults="{{creator.firstName}} left a message on a task"
          values={notification.payload}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/FILE_APPROVAL_MODE": (_ctx, notification) => {
    return {
      content: notification.payload.file.isApprovalMode ? (
        <Trans
          i18nKey="notifications.PROJECT/FILE_APPROVAL_MODE.active"
          defaults="{{creator.firstName}} enabled approval mode on file <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ) : (
        <Trans
          i18nKey="notifications.PROJECT/FILE_APPROVAL_MODE.inactive"
          defaults="{{creator.firstName}} disabled approval mode on file <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/FILE_ASSIGNATIONS_UPDATED": (_ctx, notification) => {
    return {
      content: notification.payload.isRemoved ? (
        <Trans
          i18nKey="notifications.PROJECT/FILE_ASSIGNATIONS_UPDATED.removed"
          defaults="{{creator.firstName}} removed your assignation on file <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ) : (
        <Trans
          i18nKey="notifications.PROJECT/FILE_ASSIGNATIONS_UPDATED.updated"
          defaults="{{creator.firstName}} updated assignations on file <Highlight>{{file.name}}</Highlight>"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/FILE_DELETED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FILE_DELETED.another"
          defaults="{{creator.firstName}} deleted the file <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/FILE_LABELS_UPDATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FILE_LABELS_UPDATED.another"
          defaults="{{creator.firstName}} updated categories on file <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/FILE_MOVED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FILE_MOVED.another"
          defaults="{{creator.firstName}} moved file <Highlight>{{file.name}}</Highlight> from <Highlight>{{oldParentFolder.name}}</Highlight> to <Highlight>{{newParentFolder.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/FILE_PERMISSIONS_UPDATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FILE_PERMISSIONS_UPDATED.another"
          defaults="{{creator.firstName}} updated permissions on file <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/FILE_REMINDER": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FILE_REMINDER.another"
          defaults="The approval due date on {{file.name}} is <Highlight>{{date, relative}}</Highlight> ({{project.name}} project)"
          values={{
            ...notification.payload,
            date: new Date(notification.payload.file.due_date),
          }}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/FILE_RENAMED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FILE/FILE_RENAMED.another"
          defaults="{{creator.firstName}} renamed file from <Highlight>{{oldFile.name}}</Highlight> to <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/FINALIZE_UPLOADED_FILE": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FINALIZE_UPLOADED_FILE.another"
          defaults="{{creator.firstName}} uploaded the file <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/FINALIZE_UPLOADED_FILE_VERSION": (
    _ctx,
    notification
  ) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FINALIZE_UPLOADED_FILE_VERSION.another"
          defaults="{{creator.firstName}} uploaded a new version of file <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/REQUESTED_USERS_APPROVALS_ON_FILE": (
    _ctx,
    notification
  ) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/REQUESTED_USERS_APPROVALS_ON_FILE.another"
          defaults="{{creator.firstName}} requested approval on version <Highlight>{{file.version.name}}</Highlight> of file <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FILE/USER_APPROVAL_ADDED_TO_FILE_VERSION": (
    _ctx,
    notification
  ) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/USER_APPROVAL_ADDED_TO_FILE_VERSION.another"
          defaults="{{creator.firstName}} added his approval on version <Highlight>{{file.version.name}}</Highlight> of file <Highlight>{{file.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FOLDER/FILE_STRUCTURE_CREATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FILE_STRUCTURE_CREATED.another"
          defaults="{{creator.firstName}} created the file structure on folder <Highlight>{{folder.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FOLDER/FOLDER_ASSIGNATIONS_UPDATED": (
    _ctx,
    notification
  ) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FOLDER_ASSIGNATIONS_UPDATED.another"
          defaults="{{creator.firstName}} updated assignations on folder <Highlight>{{folder.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FOLDER/FOLDER_CREATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FOLDER_CREATED.another"
          defaults="{{creator.firstName}} created the folder <Highlight>{{folder.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FOLDER/FOLDER_DELETED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FOLDER_DELETED.another"
          defaults="{{creator.firstName}} removed the folder <Highlight>{{folder.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FOLDER/FOLDER_LABELS_UPDATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FOLDER_LABELS_UPDATED.another"
          defaults="{{creator.firstName}} updated categories of folder <Highlight>{{folder.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FOLDER/FOLDER_MOVED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FOLDER_MOVED.another"
          defaults="{{creator.firstName}} moved the folder <Highlight>{{folder.name}}</Highlight> from <Highlight>{{oldParentFolder.name}}</Highlight> to <Highlight>{{newParentFolder.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FOLDER/FOLDER_PERMISSIONS_UPDATED": (
    _ctx,
    notification
  ) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FOLDER_PERMISSIONS_UPDATED.another"
          defaults="{{creator.firstName}} updated permissions on folder <Highlight>{{folder.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/DOCUMENT/FOLDER/FOLDER_RENAMED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FOLDER_RENAMED.another"
          defaults="{{creator.firstName}} renamed folder from <Highlight>{{oldFolder.name}}</Highlight> to <Highlight>{{folder.name}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/MISC/PROJECT_DELETED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/MISC/PROJECT_DELETED.me"
          defaults="{{creator.firstName}} deleted <Highlight>{{project.name}}</Highlight> project"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/MISC/ROLE_UPDATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/MISC/ROLE_UPDATED.me"
          defaults="{{creator.firstName}} updated your role from <Highlight><OldRole /></Highlight> to <Highlight><NewRole /></Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
            NewRole: (
              <RoleTranslation
                type="PROJECT"
                role={notification.payload.newRole as ProjectRoleId}
              />
            ),
            OldRole: (
              <RoleTranslation
                type="PROJECT"
                role={notification.payload.oldRole as ProjectRoleId}
              />
            ),
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/MISC/TASKS_CREATED_FROM_SPREADSHEET": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/MISC/TASKS_CREATED_FROM_SPREADSHEET.me"
          defaults="{{creator.firstName}} has created <Highlight>{{tasksCreated}}</Highlight>"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/FINALIZE_UPLOADED_TASK_ATTACHMENT": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/FINALIZE_UPLOADED_TASK_ATTACHMENT.another"
          defaults="{{creator.firstName}} uploaded the attachment {{attachment.name}} on <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/SUBTASK_CREATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/SUBTASK_CREATED.another"
          defaults="{{creator.firstName}} created the substask “{{subtask.description}}” to <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/SUBTASK_DELETED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/SUBTASK_CREATED.another"
          defaults="{{creator.firstName}} deleted the substask “{{subtask.description}}” of <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/SUBTASK_UPDATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/SUBTASK_UPDATED.another"
          defaults="{{creator.firstName}} updated the substask “{{subtask.description}}” of <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/TASK_ARCHIVE_UPDATED": (_ctx, notification) => {
    return {
      content: notification.payload.task.isArchived ? (
        <Trans
          i18nKey="notifications.PROJECT/TASK_ARCHIVE_UPDATED.archived"
          defaults="{{creator.firstName}} archived <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ) : (
        <Trans
          i18nKey="notifications.PROJECT/TASK_ARCHIVE_UPDATED.unarchived"
          defaults="{{creator.firstName}} unarchived <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/TASK_ASSIGNATIONS_UPDATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/TASK_ASSIGNATIONS_UPDATED.another"
          defaults="{{creator.firstName}} updated the assignations of <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/TASK_ATTACHEMENT_DELETED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/TASK_ATTACHEMENT_DELETED.another"
          defaults="{{creator.firstName}} deleted the attachment {{attachment.name}} on <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/TASK_CREATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/TASK_CREATED.another"
          defaults="{{creator.firstName}} created the <Highlight>Task “{{task.description}}”</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/TASK_FILE_VERSIONS_UPDATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/TASK_FILE_VERSIONS_UPDATED.another"
          defaults="{{creator.firstName}} updated files linked with <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/TASK_LABELS_UPDATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/TASK_LABELS_UPDATED.another"
          defaults="{{creator.firstName}} updated categories of <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/TASK_REMINDER": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/TASK_REMINDER.another"
          defaults="Task {{task.number}} end date is <Highlight>{{date, relative}}</Highlight> ({{project.name}} project)"
          values={{
            ...notification.payload,
            date: new Date(notification.payload.task.end_date),
          }}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/TASK_UPDATED": (_ctx, notification) => {
    return {
      content: (
        <Trans
          i18nKey="notifications.PROJECT/TASK/TASK_UPDATED.another"
          defaults="{{creator.firstName}} updated the <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
  "PROJECT/TASK/TASK_VALIDATION_UPDATED": (_ctx, notification) => {
    return {
      content: notification.payload.task.isValidated ? (
        <Trans
          i18nKey="notifications.PROJECT/TASK_VALIDATION_UPDATED.validated"
          defaults="{{creator.firstName}} validated <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ) : (
        <Trans
          i18nKey="notifications.PROJECT/TASK_VALIDATION_UPDATED.unvalidated"
          defaults="{{creator.firstName}} unvalidated <Highlight>Task {{task.number}}</Highlight> ({{project.name}} project)"
          values={notification.payload}
          components={{
            Highlight: <Text size="inherit" weight="medium" tone="info" />,
          }}
        />
      ),
      link: notificationsLinkMap[notification.type](notification).link,
    };
  },
});

export { notificationsMap };
